import { useEffect, useCallback } from 'react';

interface MediaPreviewProps {
  type: 'pdf' | 'video' | 'image';
  url: string;
  title: string;
  onClose: () => void;
}

export default function MediaPreview({ type, url, title, onClose }: MediaPreviewProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const isMockUrl = !url || url === '#';

  const renderContent = () => {
    if (isMockUrl) {
      const typeLabel = type === 'pdf' ? 'PDF文档' : type === 'video' ? '视频文件' : '图片文件';
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-white/60">
          <div className="w-16 h-16 flex items-center justify-center">
            <i className={`${type === 'pdf' ? 'ri-file-pdf-line' : type === 'video' ? 'ri-video-line' : 'ri-image-line'} text-5xl`} />
          </div>
          <p className="text-base font-medium">文件预览</p>
          <p className="text-sm text-white/40 max-w-sm text-center">
            {title ? `「${title}」` : ''}的{typeLabel}预览功能已就绪。<br />
            接入真实文件后将自动展示内容。
          </p>
        </div>
      );
    }

    switch (type) {
      case 'pdf':
        return (
          <iframe
            src={url}
            title={title}
            className="w-full h-full rounded-lg"
            style={{ border: 'none' }}
          />
        );
      case 'video':
        return (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-full max-h-full rounded-lg"
            style={{ maxHeight: '80vh' }}
          >
            您的浏览器不支持视频播放
          </video>
        );
      case 'image':
        return (
          <img
            src={url}
            alt={title}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90" />

      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-white/8 hover:bg-primary-500/50 text-white transition-all duration-200 z-30 cursor-pointer"
      >
        <i className="ri-close-line text-xl" />
      </button>

      {/* Title */}
      {title && (
        <div className="absolute top-5 left-5 z-30">
          <span className="text-white/70 text-sm bg-black/40 px-3 py-1.5 rounded-full">
            {title}
          </span>
        </div>
      )}

      {/* Content */}
      <div
        className="relative z-10"
        style={type === 'video' ? { width: '75vw', aspectRatio: '16/9', maxHeight: '85vh' } : type === 'pdf' ? { width: '90vw', height: '90vh' } : { maxWidth: '90vw', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {renderContent()}
      </div>
    </div>
  );
}